/**
 * @fileoverview ParcelPuffin POS Privacy Policy registry data.
 *
 * Hand-migrated faithfully from
 * `apps/pos-web/src/portals/marketing-website/pages/PrivacyPolicyPage.jsx`
 * (the canonical longest version — used as the superset baseline).
 *
 * Includes pos-only sections (SMS Communications, California Privacy
 * Rights, Children's Privacy) reflecting Twilio A2P 10DLC compliance
 * + CCPA / COPPA scope. Other apps' registries omit those sections.
 */

/** @type {import('../../types.js').LegalDocument} */
export const privacy = Object.freeze({
  appKey: 'parcel',
  documentKey: 'privacy',
  frontmatter: {
    title: 'Privacy Policy',
    productName: 'ParcelPuffin POS',
    legalEntity: 'D Town Retail, LLC',
    effectiveDate: '2025-12-17',
    contactEmail: 'privacy@parcelpuffin.com',
    canonicalUrl: 'https://parcelpuffin.com/privacy',
    ogImage: 'https://parcelpuffin.com/images/og-privacy.jpg',
    description:
      'Privacy policy for ParcelPuffin POS. Learn how we collect, use, and protect your personal information.',
  },
  sections: [
    {
      id: 'introduction',
      title: 'Introduction',
      order: 1,
      blocks: [
        {
          type: 'paragraph',
          text: '{productName} ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our point-of-sale software and related services (the "Service").',
        },
        {
          type: 'paragraph',
          text: 'By using our Service, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies, please do not use our Service.',
        },
      ],
    },
    {
      id: 'information-we-collect',
      title: 'Information We Collect',
      order: 2,
      blocks: [
        {
          type: 'subsection',
          title: 'Personal Information',
          blocks: [
            {
              type: 'paragraph',
              text: 'We may collect personally identifiable information, including but not limited to:',
            },
            {
              type: 'list',
              items: [
                'Name, email address, and phone number',
                'Business name and address',
                'Billing and payment information',
                'Account credentials',
                'Employee information you enter into the system',
              ],
            },
          ],
        },
        {
          type: 'subsection',
          title: 'Usage Data',
          blocks: [
            {
              type: 'paragraph',
              text: 'We automatically collect certain information when you use our Service, including:',
            },
            {
              type: 'list',
              items: [
                'Device information (browser type, operating system)',
                'IP address and location data',
                'Pages visited and features used',
                'Transaction and sales data processed through the Service',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'how-we-use-your-information',
      title: 'How We Use Your Information',
      order: 3,
      blocks: [
        { type: 'paragraph', text: 'We use the information we collect to:' },
        {
          type: 'list',
          items: [
            'Provide, maintain, and improve our Service',
            'Process transactions and send related information',
            'Send you technical notices, updates, and security alerts',
            'Respond to your comments, questions, and customer service requests',
            'Communicate with you about products, services, and events',
            'Monitor and analyze trends, usage, and activities',
            'Detect, investigate, and prevent fraudulent transactions and abuse',
            'Comply with legal obligations',
          ],
        },
      ],
    },
    {
      id: 'sms-communications',
      title: 'SMS Communications',
      order: 4,
      blocks: [
        {
          type: 'paragraph',
          text: 'When you provide your phone number during account registration, you consent to receive text messages from {productName} for the following purposes:',
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Account Verification',
              text: 'One-time codes to verify your identity during signup and login',
            },
            {
              strong: 'Security Alerts',
              text: 'Notifications about suspicious activity or security events on your account',
            },
            {
              strong: 'Billing Notifications',
              text: 'Alerts about payment processing, subscription changes, or billing issues',
            },
            {
              strong: 'Important Account Updates',
              text: 'Critical information about service changes affecting your account',
            },
          ],
        },
        {
          type: 'paragraph',
          text: 'Message and data rates may apply. Message frequency varies based on account activity. You can opt out of SMS communications at any time by contacting support, though this may affect your ability to use certain security features.',
        },
        {
          type: 'paragraph',
          text: 'We do not send marketing or promotional messages via SMS. We do not share your phone number with third parties for marketing purposes.',
        },
      ],
    },
    {
      id: 'data-sharing-and-disclosure',
      title: 'Data Sharing and Disclosure',
      order: 5,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may share your information in the following circumstances:',
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Service Providers',
              text: 'With third-party vendors who perform services on our behalf (payment processing, email delivery, SMS delivery, cloud hosting)',
            },
            {
              strong: 'Business Transfers',
              text: 'In connection with a merger, acquisition, or sale of assets',
            },
            {
              strong: 'Legal Requirements',
              text: 'When required by law or to respond to legal process',
            },
            {
              strong: 'Protection of Rights',
              text: 'To protect the rights, property, and safety of {productName}, our users, or others',
            },
          ],
        },
        { type: 'paragraph', text: 'We do not sell your personal information to third parties.' },
      ],
    },
    {
      id: 'data-security',
      title: 'Data Security',
      order: 6,
      blocks: [
        {
          type: 'paragraph',
          text: 'We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:',
        },
        {
          type: 'list',
          items: [
            'Encryption of data in transit and at rest',
            'Regular security assessments and audits',
            'Access controls and authentication requirements',
            'Employee training on data protection',
          ],
        },
        {
          type: 'paragraph',
          text: 'However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.',
        },
      ],
    },
    {
      id: 'data-retention',
      title: 'Data Retention',
      order: 7,
      blocks: [
        {
          type: 'paragraph',
          text: 'We retain your personal information for as long as your account is active or as needed to provide you services. We will retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements.',
        },
        {
          type: 'paragraph',
          text: 'Transaction data may be retained for up to seven (7) years for tax and accounting purposes.',
        },
      ],
    },
    {
      id: 'your-rights-and-choices',
      title: 'Your Rights and Choices',
      order: 8,
      blocks: [
        {
          type: 'paragraph',
          text: 'Depending on your location, you may have the following rights:',
        },
        {
          type: 'list',
          items: [
            {
              strong: 'Access',
              text: 'Request a copy of the personal information we hold about you',
            },
            { strong: 'Correction', text: 'Request correction of inaccurate personal information' },
            {
              strong: 'Deletion',
              text: 'Request deletion of your personal information, subject to certain exceptions',
            },
            { strong: 'Opt-Out', text: 'Opt out of certain data processing activities' },
            { strong: 'Portability', text: 'Request a portable copy of your data' },
          ],
        },
        {
          type: 'paragraph',
          text: 'To exercise these rights, please contact us at {contactEmail}.',
        },
      ],
    },
    {
      id: 'california-privacy-rights',
      title: 'California Privacy Rights (CCPA)',
      order: 9,
      blocks: [
        {
          type: 'paragraph',
          text: 'California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt-out of the sale of personal information.',
        },
        {
          type: 'paragraph',
          text: 'We do not sell personal information. To exercise your CCPA rights, contact us at {contactEmail}.',
        },
      ],
    },
    {
      id: 'childrens-privacy',
      title: "Children's Privacy",
      order: 10,
      blocks: [
        {
          type: 'paragraph',
          text: 'Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us.',
        },
      ],
    },
    {
      id: 'changes-to-this-policy',
      title: 'Changes to This Policy',
      order: 11,
      blocks: [
        {
          type: 'paragraph',
          text: 'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" at the top. We encourage you to review this Privacy Policy periodically.',
        },
      ],
    },
    {
      id: 'contact-us',
      title: 'Contact Us',
      order: 12,
      blocks: [
        {
          type: 'paragraph',
          text: 'If you have questions about this Privacy Policy or our privacy practices, please contact us at:',
        },
        { type: 'contact' },
      ],
    },
  ],
});
